import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef, useMemo } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "..";
import MasterLayout from "../masterLayout/MasterLayout";
import Swal from "sweetalert2";
import Select from "react-select";
import { Modal, Button } from "react-bootstrap";
import Switch from "react-switch";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {
  GoogleMap,
  Marker,
  Circle,
  useLoadScript,
} from "@react-google-maps/api";

// 📍 Mapbox Token (replace with your env variable if needed)
const MAPBOX_TOKEN = "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";;

/**
 * Helper function: fetch coordinates from address using Mapbox
 */
const fetchCoordinates = async (address) => {
  if (!address) return {};
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        address
      )}.json?access_token=${MAPBOX_TOKEN}&country=GB&limit=1`
    );
    const data = await res.json();
    if (data.features && data.features.length > 0) {
      return {
        latitude: data.features[0].center[1],
        longitude: data.features[0].center[0],
      };
    }
  } catch (err) {
    console.error("Error fetching coordinates:", err);
  }
  return {};
};

const ViewCustomerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBtQlR_uF7q-S8TQblCzLw7Fy7BvyIeXAg",
    libraries: ["places"],
  });

  const [customerData, setCustomerData] = useState({});
  const [stats, setStats] = useState({ jobs: [], quotes: [], invoices: [] });
  const [message, setMessage] = useState({ status: false, reason: "", desc: "" });
  const [activeTab, setActiveTab] = useState("invoices");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalQuoteId, setModalQuoteId] = useState(null);
  const [createJob, setCreateJob] = useState(false);
  const [referenceNo, setReferenceNo] = useState("");

  // Job form
  const [electricians, setElectricians] = useState([]);
  const [selectedElectricians, setSelectedElectricians] = useState([]);
  const [workforce, setWorkforce] = useState([]);
  const [selectedWorkforceIds, setSelectedWorkforceIds] = useState([]);
  const [jobDateSet, setJobDateSet] = useState(new Date().toISOString().slice(0, 16));
  const [isJobLocationCustomerAddress, setIsJobLocationCustomerAddress] = useState(true);
  const [jobLocation, setJobLocation] = useState("");
  const [jobLocationSuggestions, setJobLocationSuggestions] = useState([]);
  const [postCode, setPostCode] = useState("");
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 51.5074, lng: -0.1276 });
  const [enableGeofencing, setEnableGeofencing] = useState(false);
  const [radius, setRadius] = useState(100);
  const [attachments, setAttachments] = useState([]);

  // Quill
  const jobDescriptionQuillRef = useRef(null);
  const jobDescriptionQuillInstance = useRef(null);

  const jobLocationInputRef = useRef(null);

  /** -------------------- INITIAL FETCH -------------------- */
  useEffect(() => {
    fetchCustomer();
    fetchDropdowns();
  }, []);

  const fetchCustomer = async () => {
    try {
      const res = await fetch(`${API_URL}/api/customer/fetch-single/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch customer");

      setCustomerData(data.customer);
      setStats({
        jobs: data.job || [],
        quotes: data.quote || [],
        invoices: data.invoice || [],
      });
    } catch (err) {
      setMessage({
        status: true,
        reason: "error",
        desc: err.message,
      });
    }
  };

  const fetchDropdowns = async () => {
    try {
      const res1 = await fetch(`${API_URL}/api/user/fetch-electrician`);
      const data1 = await res1.json();
      setElectricians(data1.data.map((e) => ({ label: e.name, value: e.id })));
    } catch (error) {
      console.error("Dropdown fetch error:", error);
    }
  };

  /** -------------------- MAP AND LOCATION -------------------- */
  // Suggestions from Mapbox
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!jobLocation || isJobLocationCustomerAddress) return;
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            jobLocation
          )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=GB`
        );
        const data = await res.json();
        setJobLocationSuggestions(data.features || []);
      } catch (err) {
        console.error("Mapbox suggestion error:", err);
      }
    };
    const delay = setTimeout(fetchSuggestions, 400);
    return () => clearTimeout(delay);
  }, [jobLocation, isJobLocationCustomerAddress]);

  // Update map based on job/customer address
  useEffect(() => {
    const updateMap = async () => {
      const address = isJobLocationCustomerAddress
        ? customerData.address
        : jobLocation;

      if (!address) return;

      const coords = await fetchCoordinates(address);
      if (coords.latitude && coords.longitude) {
        setMarkerPosition({ lat: coords.latitude, lng: coords.longitude });
        setMapCenter({ lat: coords.latitude, lng: coords.longitude });
      }
    };
    updateMap();
  }, [isJobLocationCustomerAddress, jobLocation, customerData.address]);

  /** -------------------- QUILL INIT -------------------- */
  useEffect(() => {
    if (jobDescriptionQuillRef.current && !jobDescriptionQuillInstance.current) {
      jobDescriptionQuillInstance.current = new Quill(jobDescriptionQuillRef.current, {
        theme: "snow",
        placeholder: "Write job description...",
      });
    }
  }, [showModal]);

  /** -------------------- ACTION HANDLERS -------------------- */
  const handleStatusChange = async () => {
    setIsSubmitting(true);
    const newStatus = customerData.status === "active" ? "inactive" : "active";
    try {
      await fetch(`${API_URL}/api/customer/change-status/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      setCustomerData({ ...customerData, status: newStatus });
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateJob = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const quote = stats.quotes.find((q) => q.id === modalQuoteId);
    if (!user || !quote) return;

    const formData = new FormData();
    formData.append("client_name", customerData.name);
    formData.append("phone", customerData.phone);
    formData.append("device_Type", "N/A");
    formData.append("job_cost", quote.amount);
    formData.append(
      "address",
      isJobLocationCustomerAddress ? customerData.address : jobLocation
    );
    formData.append("postcode", postCode || customerData.postcode);
    formData.append("map_location", String(markerPosition.lat)+','+String(markerPosition.lng));
    formData.append("job_posted_by", user.id);
    formData.append("service_required", quote.service_required || "General");
    formData.append(
      "job_posted_to",
      selectedElectricians.map((e) => e.value).join(",")
    );
    formData.append("assigned_by", user.id);
    formData.append(
      "description",
      jobDescriptionQuillInstance.current?.root.innerHTML || ""
    );
    formData.append(
      "workforce_ids",
      selectedWorkforceIds.map((w) => w.value).join(",")
    );
    formData.append("enableGeofencing", enableGeofencing);
    formData.append("radius", radius);
    formData.append("quoteId", modalQuoteId);
    formData.append("referenceNo", referenceNo || "");
    formData.append("job_date_set", jobDateSet);

    if (attachments.length > 0) {
      for (const file of attachments) formData.append("attachments", file);
    }

    for (const [key, value] of formData.entries()) console.log(`${key}:`, value);

    const res = await fetch(`${API_URL}/api/job/create-for-quote`, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message);
  };

  const handleSubmit = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/invoice/convert-to-invoice/${modalQuoteId}`,
        { method: "PUT" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      if (createJob) {
        if (!selectedElectricians.length) {
          Swal.fire({
            icon: "warning",
            title: "Missing Electricians",
            text: "Please assign at least one electrician.",
          });
          return;
        }

        await handleCreateJob();
        Swal.fire({
          icon: "success",
          title: "Job Created",
          text: "Job created and quote converted to invoice successfully.",
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Invoice Created",
          text: "Quote converted to invoice successfully.",
        });
      }

      fetchCustomer();
      setShowModal(false);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    }
  };
  const formatAmount = (amount) => { if (!amount) return "N/A"; return `£${parseFloat(amount).toFixed(2)}`; };
  const handleEdit = () => { navigate(`/customers/edit/${id}`); };
  const formatDate = (date) => { if (!date) return "N/A"; return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", }); };
  // 🔥 Mutual Exclusion Logic (Electrician <-> Workforce)
const electricianSelectOptions = useMemo(() => {
  return electricians.filter((opt) => {
    const selectedInWorkforce = selectedWorkforceIds.some(w => w.value === opt.value);
    const selectedInElectrician = selectedElectricians.some(e => e.value === opt.value);

    if (selectedInElectrician) return true; // keep selected electrician
    return !selectedInWorkforce;
  });
}, [electricians, selectedElectricians, selectedWorkforceIds]);

const workforceSelectOptions = useMemo(() => {
  return electricians.filter((opt) => {
    const selectedInElectrician = selectedElectricians.some(e => e.value === opt.value);
    const selectedInWorkforce = selectedWorkforceIds.some(w => w.value === opt.value);

    if (selectedInWorkforce) return true; // keep selected workforce
    return !selectedInElectrician;
  });
}, [electricians, selectedElectricians, selectedWorkforceIds]);

  return (
    <MasterLayout>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            {/* Customer Details Card */}
            <div className="card shadow-sm border-0 mb-4 animate__animated animate__fadeIn">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                <p
                  style={{ fontWeight: "500", fontSize: 20 }}
                  className="mb-0 text-secondary"
                >
                  Customer Details
                </p>
                <button
                  onClick={handleEdit}
                  className="btn btn-outline-primary btn-sm"
                  title="Edit Customer"
                >
                  <Icon icon="simple-line-icons:pencil" className="me-2" />
                  Edit
                </button>
              </div>
              <div className="card-body p-3">
                {message.status && (
                  <div
                    className={`alert alert-${
                      message.reason === "error" ? "danger" : "success"
                    } alert-dismissible fade show mb-4`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <strong className="me-2 text-muted">Name:</strong>
                      <span>{customerData.name || "N/A"}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <strong className="me-2 text-muted">Email:</strong>
                      <span>{customerData.email || "N/A"}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <strong className="me-2 text-muted">Phone:</strong>
                      <span>{customerData.phone || "N/A"}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <strong className="me-2 text-muted">Address:</strong>
                      <span>{customerData.address || "N/A"}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <strong className="me-2 text-muted">Postcode:</strong>
                      <span>{customerData.postcode || "N/A"}</span>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="row d-flex align-items-end">
                      <div className="col-md-6 text-start">
                        <strong className="me-2 text-muted">
                          Status:
                          <label
                            className="form-check-label"
                            htmlFor="statusSwitch"
                          >
                            {customerData.status === "active"
                              ? "Active"
                              : "Inactive"}
                          </label>
                        </strong>
                      </div>
                      <div className="col-md-6 text-end">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="statusSwitch"
                            checked={customerData.status === "active"}
                            onChange={handleStatusChange}
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics Card */}
            <div className="card shadow-sm border-0 mb-4 mt-3 animate__animated animate__fadeIn">
              <div className="card-header bg-white border-bottom py-3">
                <p
                  style={{ fontWeight: "500", fontSize: 20 }}
                  className="mb-0 text-secondary"
                >
                  Customer Statistics
                </p>
              </div>
              <div className="simplex card-body p-4">
                <div className="row text-center g-4 pt-3">
                  <div className="col-md-4">
                    <h3 className="h6 mb-2 text-muted">Jobs</h3>
                    <p className="display-5 text-secondary fw-bold">
                      {stats.jobs.length}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <h3 className="h6 mb-2 text-muted">Quotes</h3>
                    <p className="display-5 text-secondary fw-bold">
                      {stats.quotes.length}
                    </p>
                  </div>
                  <div className="col-md-4">
                    <h3 className="h6 mb-2 text-muted">Invoices</h3>
                    <p className="display-5 text-secondary fw-bold">
                      {stats.invoices.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs for Invoices/Quotes/Jobs */}
            <div className="card shadow-sm border-0 animate__animated animate__fadeIn mt-3">
              <div className="card-header bg-white border-bottom py-3">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "invoices" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("invoices")}
                    >
                      Invoices
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "quotes" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("quotes")}
                    >
                      Quotes
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "jobs" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("jobs")}
                    >
                      Jobs
                    </button>
                  </li>
                </ul>
              </div>
              <div className="card-body p-4">
                {/* Invoices */}
                {activeTab === "invoices" && (
                  <div className="row g-3">
                    {stats.invoices.length > 0 ? (
                      stats.invoices.map((invoice) => (
                        <div key={invoice.id} className="col-md-6 col-lg-4">
                          <div className="card shadow-sm border-0 h-100 hover-shadow">
                            <Link to={"/invoices/preview/" + invoice.id}>
                              <div className="card-body">
                                <h6 className="card-title text-primary">
                                  Invoice #{invoice.id}
                                </h6>
                                <p className="mb-1">
                                  <strong>Amount:</strong>{" "}
                                  {formatAmount(invoice.amount)}
                                </p>
                                <p className="mb-1">
                                  <strong>Date:</strong>{" "}
                                  {formatDate(invoice.issue_date)}
                                </p>
                                <p className="mb-0">
                                  <strong>Status:</strong>{" "}
                                  {invoice.status || "N/A"}
                                </p>
                              </div>
                            </Link>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted p-4">
                        No invoices available.
                      </div>
                    )}
                  </div>
                )}

                {/* Quotes */}
                {activeTab === "quotes" && (
                  <div className="row g-3">
                    {stats.quotes.length > 0 ? (
                      stats.quotes.map((quote) => (
                        <div key={quote.id} className="col-md-6 col-lg-4">
                          <div className="card shadow-sm border-0 h-100 hover-shadow">
                            <Link to={"/quotes/preview/" + quote.id}>
                              <div className="card-body">
                                <h6 className="card-title text-primary">
                                  Quote #{quote.id}
                                </h6>
                                <p className="mb-1">
                                  <strong>Amount:</strong>{" "}
                                  {formatAmount(quote.amount)}
                                </p>
                                <p className="mb-1">
                                  <strong>Issue Date:</strong>{" "}
                                  {formatDate(quote.issue_date)}
                                </p>
                                <button
                                  className="btn btn-sm btn-primary mt-2"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setModalQuoteId(quote.id);
                                    setShowModal(true);
                                  }}
                                >
                                  Convert to Invoice
                                </button>
                              </div>
                            </Link>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted p-4">
                        No quotes available.
                      </div>
                    )}
                  </div>
                )}

                {/* Jobs */}
                {activeTab === "jobs" && (
                  <div className="row g-3">
                    {stats.jobs.length > 0 ? (
                      stats.jobs.map((job) => (
                        <div key={job.id} className="col-md-6 col-lg-4">
                          <div className="card shadow-sm border-0 h-100 hover-shadow">
                            <Link to={"/jobs/view/" + job.id}>
                              <div className="card-body">
                                <h6 className="card-title text-primary">
                                  Job #{job.id}
                                </h6>

                                <p className="mb-1">
                                  <strong>Job Date:</strong>{" "}
                                  {formatDate(job.createdAt)}
                                </p>
                                <p className="mb-0">
                                  <strong>Status:</strong>{" "}
                                  {job.isClosed
                                    ? "Closed"
                                    : job.status || "N/A"}
                                </p>
                              </div>
                            </Link>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted p-4">
                        No jobs available.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Modal
        size="lg"
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span style={{fontSize: 22}}> Convert Quote to Invoice</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              checked={createJob}
              onChange={(e) => setCreateJob(e.target.checked)}
            />
            <label className="form-check-label">Also create a Job</label>
          </div>

          {!createJob ? (
            <p>Are you sure you want to convert this quote into an invoice?</p>
          ) : (
            <>
              {/* Reference + Electrician + Workforce */}
              <div className="mb-3">
                <label>Reference No</label>
                <input
                  className="form-control"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label>Assign Electricians *</label>
                <Select
                isMulti
                options={electricianSelectOptions}
                value={selectedElectricians}
                onChange={setSelectedElectricians}
                placeholder="Select Electricians"
              />
              </div>

              <div className="mb-3">
                <label>Assign Workforce</label>
                <Select
                isMulti
                options={workforceSelectOptions}
                value={selectedWorkforceIds}
                onChange={setSelectedWorkforceIds}
                placeholder="Select Workforce"
              />
              </div>

              <div className="mb-3">
                <label>Job Date</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={jobDateSet}
                  onChange={(e) => setJobDateSet(e.target.value)}
                />
              </div>

              {/* Location */}
              <div className="mb-3 d-flex align-items-center gap-2">
                <label>Use Customer Address</label>
                <Switch
                  checked={isJobLocationCustomerAddress}
                  onChange={() =>
                    setIsJobLocationCustomerAddress(!isJobLocationCustomerAddress)
                  }
                />
              </div>

              {!isJobLocationCustomerAddress && (
                <>
                  <div className="mb-3 position-relative">
                    <label>Job Location</label>
                    <input
                      ref={jobLocationInputRef}
                      type="text"
                      className="form-control"
                      value={jobLocation}
                      onChange={(e) => setJobLocation(e.target.value)}
                      placeholder="Search job location..."
                    />
                    {jobLocationSuggestions.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100 shadow mt-1"
                        style={{
                          zIndex: 1050,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {jobLocationSuggestions.map((place, i) => (
                          <li
                            key={i}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setJobLocation(place.place_name);
                              setJobLocationSuggestions([]);
                            }}
                          >
                            {place.place_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mb-3">
                    <label>Job Postcode</label>
                    <input
                      type="text"
                      className="form-control"
                      value={postCode}
                      onChange={(e) => setPostCode(e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* Attachments */}
              <div className="mb-3">
                <label>Attachments</label>
                <input
                  type="file"
                  multiple
                  className="form-control"
                  onChange={(e) => setAttachments(e.target.files)}
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <label>Job Description</label>
                <div ref={jobDescriptionQuillRef} />
              </div>

              {/* Geofencing */}
              <div className="mb-3 d-flex align-items-center gap-3">
                <label>Enable Geofencing</label>
                <Switch
                  checked={enableGeofencing}
                  onChange={() => setEnableGeofencing(!enableGeofencing)}
                />
                {enableGeofencing && (
                  <input
                    type="number"
                    className="form-control w-auto"
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    placeholder="Radius (m)"
                  />
                )}
              </div>

              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={{
                    width: "100%",
                    height: "350px",
                    borderRadius: "8px",
                  }}
                  center={mapCenter}
                  zoom={13}
                >
                  {markerPosition && <Marker position={markerPosition} />}
                  {enableGeofencing && (
                    <Circle
                      center={markerPosition}
                      radius={radius}
                      options={{
                        strokeColor: "#FF0000",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#FF0000",
                        fillOpacity: 0.35,
                      }}
                    />
                  )}
                </GoogleMap>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </MasterLayout>
  );
};

export default ViewCustomerPage;
